import { useMemo, useState } from "react";
import {
  useListMemberCharges,
  useListMemberEntitlements,
  useListInventoryItems,
  useDeleteMemberCharge,
  getListMemberChargesQueryKey,
  type MemberCharge,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { format, differenceInCalendarDays } from "date-fns";
import { CalendarPlus, Clock, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Booking tab — mirrors the Plan tab's structure: a header action to create,
// a complimentary-hours summary on top, then a table of this account's
// bookings. Booking creation reuses the account-scoped NewBookingSheet owned
// by account-detail.tsx (opened via the onNewBooking callback) so there is a
// single sheet instance and a single POST /booking contract.
interface Props {
  accountId: number;
  onNewBooking: () => void;
}

// Phase 29: Booking tab now covers both room bookings (Hourly_Booking) and
// day-pass sales (Daily_Booking). The filter includes both source types so
// pass charges are visible here alongside room charges.
const BOOKING_SOURCE_TYPES = new Set(["Hourly_Booking", "Daily_Booking"]);
// Complimentary hours come from these two synthetic entitlement items.
const ROOM_HOUR_ITEMS = new Set(["Conference_Room_Hours", "Meeting_Room_Hours"]);

function humanize(s: string): string {
  return s.replace(/_/g, " ");
}

// A pass bills per person per day, so member_charges.quantity holds days × seats.
// Labelling that "days" is wrong the moment a pass covers more than one person —
// a 1-day pass for 2 people is quantity 2, which read as "2 days". Recover the
// two factors from the booked window: days is the calendar span, seats is what's
// left over. Works for legacy single-seat rows too (seats resolves to 1).
function passUnits(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  qty: number,
): { days: number; seats: number } | null {
  if (!startIso) return null;
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : start;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.max(1, differenceInCalendarDays(end, start) + 1);
  const seats = Math.max(1, Math.round(qty / days));
  return { days, seats };
}

function passQtyLabel(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  qty: number,
): string {
  const u = passUnits(startIso, endIso, qty);
  if (!u) return `${qty} day${qty !== 1 ? "s" : ""}`;
  const days = `${u.days} day${u.days !== 1 ? "s" : ""}`;
  return u.seats > 1 ? `${days} × ${u.seats} seats` : days;
}

function formatINR(amount: number): string {
  return `₹${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Paid":
      return "default";
    case "Disputed":
      return "destructive";
    case "Waived":
      return "outline";
    default:
      return "secondary"; // Pending, Billed
  }
}

export function BookingsList({ accountId, onNewBooking }: Props) {
  // Complimentary hours = active member_entitlements (e.g. Conference Room
  // Hours, Meeting Room Hours). itemId resolves to a human label via the
  // inventory items list.
  const { data: entitlements = [], isLoading: entLoading } =
    useListMemberEntitlements({ memberId: accountId, activeOnly: true });
  const { data: items = [], isLoading: itemsLoading } = useListInventoryItems();
  const itemNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const it of items) map[it.id] = it.name;
    return map;
  }, [items]);

  // Only Conference/Meeting Room hour quotas count as "complimentary hours";
  // print/stationery quotas (other entitlement items) are not shown here.
  const roomEntitlements = useMemo(
    () =>
      entitlements.filter((e) =>
        ROOM_HOUR_ITEMS.has(itemNameById[e.itemId] ?? ""),
      ),
    [entitlements, itemNameById],
  );

  // Bookings are member_charges with a booking source type. The list hook
  // returns the {items,total,page,pageSize} envelope — read .items.
  const { data: chargesData, isLoading: chargesLoading } = useListMemberCharges({
    memberId: accountId,
    pageSize: 200,
  });
  const bookings = useMemo<MemberCharge[]>(() => {
    const all: MemberCharge[] = chargesData?.items ?? [];
    return all
      .filter((c) => BOOKING_SOURCE_TYPES.has(c.sourceType))
      .sort(
        (a, b) =>
          new Date(b.transactionDate).getTime() -
          new Date(a.transactionDate).getTime(),
      );
  }, [chargesData]);

  // Delete a non-paid booking (e.g. an erroneous Day Pass). Paid bookings have
  // financial records and cannot be deleted (the button is hidden + server 409s).
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const deleteBooking = useDeleteMemberCharge();
  const [deleteTarget, setDeleteTarget] = useState<MemberCharge | null>(null);

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteBooking.mutateAsync({ id: deleteTarget.id });
      toast({ title: "Booking deleted." });
      queryClient.invalidateQueries({ queryKey: getListMemberChargesQueryKey() });
      setDeleteTarget(null);
    } catch {
      toast({ title: "Couldn't delete the booking.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Header action — matches the Plan tab's "Add Plan" placement. */}
      <div className="flex justify-end">
        <Button size="sm" onClick={onNewBooking}>
          <CalendarPlus className="mr-2 h-4 w-4" />
          New Booking
        </Button>
      </div>

      {/* Complimentary hours */}
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Complimentary Hours</h3>
        </div>
        {entLoading || itemsLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-md" />
            ))}
          </div>
        ) : roomEntitlements.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No complimentary hours on this account&apos;s plan(s).
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {roomEntitlements.map((e) => {
              const allowed = Number(e.quotaAllowed ?? 0);
              const remaining = Number(e.quotaRemaining ?? 0);
              const used = Number(e.quotaUsed ?? 0);
              const pctUsed =
                allowed > 0 ? Math.min(100, Math.round((used / allowed) * 100)) : 0;
              return (
                <div key={e.id} className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">
                    {humanize(itemNameById[e.itemId] ?? `Item #${e.itemId}`)}
                  </p>
                  <p className="mt-1 text-lg font-semibold">
                    {remaining}
                    <span className="text-sm font-normal text-muted-foreground">
                      {" "}
                      / {allowed} hrs left
                    </span>
                  </p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${pctUsed}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {e.periodLabel}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Bookings */}
      <section className="space-y-2">
        <h3 className="text-sm font-medium">Bookings</h3>
        {chargesLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded" />
            ))}
          </div>
        ) : bookings.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No bookings yet. Use “New Booking” to create one.
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Room / Details</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b) => {
                  const isDaily = b.sourceType === "Daily_Booking";
                  // bookingStartTime / bookingEndTime are present in the DB row
                  // but not declared in the OpenAPI MemberCharge schema yet;
                  // access via cast and guard against null before formatting.
                  const raw = b as MemberCharge & {
                    bookingStartTime?: string | null;
                    bookingEndTime?: string | null;
                  };
                  const qty = Number(b.quantity ?? 0);
                  return (
                    <TableRow key={b.id}>
                      <TableCell className="whitespace-nowrap">
                        {isDaily && raw.bookingStartTime ? (() => {
                          // Single-day pass → one date (start == end); else a range.
                          const s = format(new Date(raw.bookingStartTime), "MMM d, yyyy");
                          const e = raw.bookingEndTime
                            ? format(new Date(raw.bookingEndTime), "MMM d, yyyy")
                            : s;
                          return s === e
                            ? s
                            : `${format(new Date(raw.bookingStartTime), "MMM d")} – ${e}`;
                        })() : b.transactionDate ? (
                          format(new Date(b.transactionDate), "MMM d, yyyy")
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="max-w-[320px] truncate">
                        {b.description || "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {isDaily
                          ? passQtyLabel(raw.bookingStartTime, raw.bookingEndTime, qty)
                          : `${qty} hr`}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatINR(Number(b.total ?? 0))}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(b.status)}>{b.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Delete only a non-paid booking. Paid ones are kept (financial records). */}
                        {b.status !== "Paid" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            title="Delete booking"
                            onClick={() => setDeleteTarget(b)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <AlertDialog open={deleteTarget != null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.description || "This booking"} will be permanently removed
              and the seat freed. This can’t be undone. Paid bookings can’t be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBooking.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void confirmDelete(); }}
              disabled={deleteBooking.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBooking.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
